import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CalendarContainerProps } from 'react-datepicker';

import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import { IDatePickerProps } from './models';

import { AnyType } from '@src/models/common';
import { DefaultDatePicker } from './_DefaultDatePicker/DefaultDatePicker';
import { getRangeLabel, registerLocaleInit } from './utils';
import { InputIcon } from '../InputIcon';

import { ICON_SIZE } from '../Input/consts';
import { Box } from '../Box';
import { DatePickerCustomTimeInput } from './_CustomTimeInput/DatePickerCustomTimeInput';
import { DatePickerCustomHeader } from '@src/atoms/CustomHeader/DatePickerCustomHeader';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { DatePickerEmptyPlaceholder } from '@src/atoms/DatePicker/_EmptyPlaceholder/DatePickerEmptyPlaceholder';
import { DatePickerCustomContainer } from '@src/atoms/DatePicker/_СustomContainer';

import styled from './Datepicker.module.scss';

/** Компонент для работы с датой */
export const DatePicker = forwardRef<AnyType, IDatePickerProps>(
  (
    {
      className,
      value,
      label,
      size = 'md',
      errorMessage,
      helpMessage,
      isHelpMessage,
      format,
      id,
      postIcon,
      preIcon,
      isClearable,
      onChange,
      onFocus,
      onBlur,
      showPopperArrow = false,
      showRangeLabel = false,
      renderCustomHeader = DatePickerCustomHeader,
      // @ts-ignore
      customTimeInput = <DatePickerCustomTimeInput />,
      staticLabel,
      showTimeInput = false,
      theme = 'light',
      selectsRange,
      helperDropdownText,
      calendarContainer,
      ...rest
    },
    ref,
  ) => {
    const innerRef = useRef<AnyType>(null);
    const { isMobile } = useContext(BreakpointsContext);
    const [isFocus, setIsFocus] = useState(false);

    const rangeLabel = useMemo(() => {
      if (!showRangeLabel) return '';
      return getRangeLabel({ range: [rest.startDate, rest.endDate], showYear: true });
    }, [rest.startDate, rest.endDate, showRangeLabel]);

    const handleChange = useCallback(
      (d: Date | [Date | null, Date | null] | null, e?: React.ChangeEvent<HTMLInputElement>) => {
        if (selectsRange) {
          const v = (d as [Date | null, Date | null]).map((date) => {
            return date ? date.toISOString() : '';
          });

          onChange?.(v as [string | null, string | null], e);
          return;
        }

        const v = d ? (d as Date).toISOString() : '';
        onChange?.(v, e);
      },
      [onChange, selectsRange],
    );
    const handleFocus = useCallback(
      (e) => {
        setIsFocus(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocus(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const selected = useMemo(() => {
      if (value instanceof Date) return value;

      if (typeof value === 'string' && value !== '') {
        return new Date(value);
      }

      if (selectsRange && Array.isArray(value) && value.length) {
        const startDate = value[0];

        if ((startDate as unknown) instanceof Date) {
          return startDate as unknown as Date;
        }

        return new Date(startDate as string);
      }

      return null;
    }, [value]);

    const isSmallLabel = Boolean(value) || isFocus;
    const isPlaceholder = Boolean(!label || isSmallLabel);
    const iconSize = ICON_SIZE[size];
    const isClearableIcon = isClearable && value;

    const rootClasses = classnames(styled.root, ...getClasses(styled, { size }), {
      [styled.error]: Boolean(errorMessage),
    });
    const containerClasses = classnames(className, styled.container, styled.wrapper);
    const dayClassName = (date: Date) =>
      classnames(styled.day, rest.dayClassName?.(date), {
        [styled.dayDarkTheme]: theme === 'dark',
        [styled.dayDarkThemeMobile]: isMobile && theme === 'dark',
      });

    const showSelected = useMemo(() => {
      if (staticLabel) {
        return isFocus ? selected : undefined;
      } else {
        return selected;
      }
    }, [staticLabel, isFocus, selected]);

    const EmptyInput = useMemo(() => {
      const hasDateRangeSelection = staticLabel && (rest.startDate !== undefined || rest.endDate !== undefined);

      if (hasDateRangeSelection) {
        return DatePickerEmptyPlaceholder;
      }

      return undefined;
    }, [staticLabel, rest.startDate, rest.endDate]);

    const customContainer = useMemo(
      () =>
        calendarContainer ||
        (helperDropdownText
          ? (props: CalendarContainerProps) => (
              <DatePickerCustomContainer {...props} helperDropdownText={helperDropdownText} />
            )
          : undefined),
      [calendarContainer, helperDropdownText],
    );

    const handleCalendarClosed = useCallback(() => {
      if (staticLabel) setIsFocus(false);

      if (rest?.onCalendarClose) {
        rest?.onCalendarClose();
      }
    }, [staticLabel, rest]);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => innerRef.current!, []);

    useEffect(() => {
      registerLocaleInit();
    }, []);

    useEffect(() => {
      if (innerRef.current) {
        const input = innerRef.current.input as HTMLInputElement | undefined;
        input?.setAttribute('data-id', id);
      }
    }, [id]);

    return (
      <div
        className={classnames(rootClasses, rest.inline && styled.wrapper, className, ...getClasses(styled, { theme }))}
      >
        {rest.inline ? (
          <DefaultDatePicker
            {...rest}
            renderCustomHeader={(props) => renderCustomHeader({ ...props, theme })}
            customTimeInput={customTimeInput}
            placeholderText={rest.placeholder}
            ref={innerRef}
            dateFormat={format}
            selected={selected}
            showPopperArrow={showPopperArrow}
            showTimeInput={showTimeInput}
            dayClassName={dayClassName}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            selectsRange={selectsRange}
            customInput={EmptyInput ? <EmptyInput /> : undefined}
          />
        ) : (
          <>
            <div className={containerClasses} data-size={size} data-elem="container" data-placeholder={isPlaceholder}>
              {staticLabel || rangeLabel ? (
                <>
                  <Box style={{ whiteSpace: 'nowrap', position: 'absolute' }}>
                    {!isFocus && (rangeLabel || staticLabel)}
                  </Box>

                  {!showRangeLabel && (selected || (rest?.startDate && rest?.endDate)) && (
                    <Box className={styled.counter} />
                  )}
                </>
              ) : (
                <div data-elem="label" data-size={size} className={styled.label} data-label-small={isSmallLabel}>
                  {label}
                </div>
              )}

              {preIcon && <InputIcon config={preIcon} data-elem="preIcon" data-size={size} size={iconSize} />}

              <DefaultDatePicker
                {...rest}
                renderCustomHeader={(props) => renderCustomHeader({ ...props, theme })}
                customTimeInput={customTimeInput}
                placeholderText={rest.placeholder}
                ref={innerRef}
                dateFormat={format}
                showPopperArrow={showPopperArrow}
                showTimeInput={showTimeInput}
                selected={showSelected}
                dayClassName={dayClassName}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onCalendarClose={handleCalendarClosed}
                customInput={EmptyInput ? <EmptyInput /> : undefined}
                selectsRange={selectsRange}
                calendarContainer={customContainer}
              />

              {isClearableIcon && (
                <InputIcon
                  config={'close'}
                  size={iconSize}
                  className={styled.clearableBtn}
                  data-size={size}
                  onClick={() => {
                    handleChange(null);
                  }}
                />
              )}
              {!isClearableIcon && postIcon && <InputIcon config={postIcon} size={iconSize} data-size={size} />}
            </div>

            {isHelpMessage && (
              <>
                {errorMessage ? (
                  <div data-elem="message" data-id="errorMsg">
                    {errorMessage}
                  </div>
                ) : (
                  <div data-elem="message" data-id="helpMsg">
                    {helpMessage}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  },
);
